// Bildschirm "Stichwortkatalog" (ST-TAX-003, MVP-Kern): die Stichworte
// sehen, ein Stichwort fuer das eigene Haus vorschlagen, einen Vorschlag einer
// anderen Person freigeben, ein Stichwort beenden oder ersetzen, nach Name
// und frueherem Namen suchen.
//
// EINE HAUPTAKTION (5.2 Nr. 2): "Vorschlagen". Freigeben, Beenden, Ersetzen
// und Verwerfen sind Zweitaktionen an der Zeile; Beenden, Ersetzen und
// Verwerfen oeffnen erst eine Rueckfrage, die die Wirkung nennt (progressive
// Offenlegung, beenden statt loeschen nach 5.2.4). Die Suche ist
// Zweitaktion des Abschnitts "Stichwort suchen". Kein Hauptnavigationspunkt: erreichbar aus der
// Massenpflege, wie die Datenqualitaet.
//
// FREIGABE (K6): die Schaltflaeche ist erst frei, wenn die Person bestaetigt,
// dass das Stichwort ein Sortiment oder Thema beschreibt und kein Merkmal
// einer Person. Den eigenen Vorschlag gibt eine andere Person frei; die Zeile
// sagt das, statt einen gesperrten Knopf ohne Grund zu zeigen.
//
// Nach jedem Befehl laedt der Katalog neu, und der Fokus geht in die
// Statusregion (Muster Datenqualitaet).
//
// UMHAENGEN (Schnitt 5, K3): ein ersetztes Stichwort gibt seine Zuordnungen
// an seinen Nachfolger ab. Der Knopf legt den Massenlauf an -- das ist die
// Vorschau -- und fuehrt auf dessen Seite, wo die Wirkung in Zahlen vom
// Server steht und erst die Hauptaktion etwas aendert (Muster Kontaktsuche).
// Ob es Zuordnungen gibt und welche sichtbar sind, weiss nur der Server.

import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  Checkbox,
  Field,
  Input,
  makeStyles,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Radio,
  RadioGroup,
  Select,
  Spinner,
  Subtitle2,
  Textarea,
  tokens,
} from '@fluentui/react-components';

import type { PlainMessage } from '../access.ts';
import { keepsRequest, requestFor, type HeldRequest } from '../client-request.ts';
import type { FieldError } from '../command-client.ts';
import {
  appliedSentence,
  approveProposal,
  END_REASONS,
  endCatalogTerm,
  fetchCatalog,
  gueltigeWahl,
  requestKeyInput,
  MAXIMUM_TERM_DEFINITION_LENGTH,
  MAXIMUM_TERM_NAME_LENGTH,
  hitStatusText,
  appendCatalogPage,
  listChosenSentence,
  morePagesSentence,
  listHeading,
  listSearchSummary,
  MINIMUM_SEARCH_LENGTH,
  otherListsSentence,
  proposeTerm,
  replaceCatalogTerm,
  replacementCandidates,
  searchCatalog,
  termKind,
  type TermKind,
  termStatusText,
  GROUPWIDE_NOTE,
  needsStewardNote,
  STEWARD_CHANGE_NOTE,
  CHANGE_UNCERTAIN_MESSAGE,
  LOAD_UNREACHABLE_MESSAGE,
  MORE_UNREACHABLE_MESSAGE,
  PROPOSAL_UNCERTAIN_MESSAGE,
  SEARCH_UNREACHABLE_MESSAGE,
  type CatalogHit,
  type CatalogTermView,
  type CatalogView,
  type TermCommandResult,
} from '../taxonomy-catalog.ts';
import { createMergeBatch, massenlaufHref, PREVIEW_UNCERTAIN_MESSAGE } from '../classification-batches.ts';
import { usePflegeStyles } from '../shell/pflegeabschnitt.tsx';
import { meldungsIntent } from '../shell/use-section-commands.ts';
import { navigate, setzeAdressParameter, useAdressParameter } from '../shell/router.tsx';
import { AnlegenFormular, AnlegenOeffner, useAnlegen } from './pflege-kopf.tsx';
import { beiEscapeSchliessen } from './kontakt/plus-symbol.tsx';
import { useScreenStyles } from '../shell/screen-styles.ts';
import { Symbol } from '../shell/symbole.tsx';
import { VorgangNach } from '../shell/zustand.tsx';
import { NebenKnopf } from '../shell/nebenknopf.tsx';

const useStyles = makeStyles({
  formular: { display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem', maxWidth: '40rem' },
  // Filterzeile: Suche links, "Stichwort vorschlagen" rechts; schmal untereinander.
  werkzeug: { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: '0.75rem 1rem', marginTop: '1rem' },
  suchfeld: { flex: '1 1 16rem', minWidth: 0 },
  // Die Listenauswahl hat eine eigene Zeile ueber Suche und Hauptaktion (A7 V1): sie bestimmt die Arbeit, kein Filter.
  // Das Feld bleibt schmal, sein Hinweis laeuft breiter und bricht nicht unnoetig um (DESIGN-P3 zu #739).
  listenwahl: { minWidth: 0 },
  listenfeld: { maxWidth: '22rem' },
  // Saetze der Suchregion stehen je in eigener Zeile (Body1 ist inline; sonst klebten sie aneinander).
  satzZeile: { display: 'block' },
  // Nur fuer Hilfsmittel: die Ansage nach der Listenwahl wiederholt sonst sichtbar die Ueberschrift.
  // Absolut positioniert nimmt die Region nicht am Fluss der Karte teil und bringt keinen Abstand mit
  // (sonst sprang der Inhalt bei jeder Wahl, und die leere Hauptregion verloere ihr :empty).
  nurLeser: { position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' },
  fuss: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem 0.75rem', marginTop: '1rem', ':empty': { marginTop: 0 } },
  anderswo: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem 0.75rem', marginTop: '0.5rem' },
  // Die Lupe ist ein eigenes Bedienziel: mindestens 24 px (WCAG 2.5.8).
  lupe: { minWidth: '24px', minHeight: '24px' },
  suchform: { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '0.5rem', flex: '1 1 22rem', minWidth: 0, maxWidth: '40rem' },
  liste: { listStyle: 'none', margin: '0.5rem 0 0', padding: 0 },
  // Zeilen mit Trennlinie (v4); der Zustand steht als Pille am Namen statt als farbiger Balken.
  // Zeile wie in den Wiedervorlagen (Muster auch fuer den Rollenkatalog): links Name, Pille und Saetze,
  // rechts die Zeilenknoepfe; schmal stehen die Knoepfe darunter, Rueckfragen immer ueber die volle Breite.
  eintrag: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: '0.5rem 0.875rem',
    padding: '0.75rem 0',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    overflowWrap: 'anywhere',
    '& > div:not([role="group"])': { marginTop: 0 },
  },
  text: { flex: '1 1 20rem', minWidth: 0 },
  voll: { flex: '1 1 100%' },
  gruppenSatz: { display: 'block', marginTop: '0.25rem', color: tokens.colorNeutralForeground2 },
  namenszeile: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.25rem 0.5rem' },
  beendet: { color: tokens.colorNeutralForeground2 },
  // Eine leere Statusregion bleibt im Baum (sonst ginge die naechste Ansage verloren), traegt aber keinen Abstand.
  hinweis: { display: 'block', marginTop: '0.75rem', color: tokens.colorNeutralForeground2, ':empty': { marginTop: 0 } },
  satz: { display: 'block', marginTop: '0.25rem' },
  aktionen: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem', alignItems: 'center' },
  abschnitt: { marginTop: '1.25rem' },
  rueckfrageSpalte: { flexDirection: 'column', alignItems: 'flex-start', maxWidth: '100%' },
});

/**
 * Der Zustand eines Stichworts als Pille (v4 .pill): ein kurzes Wort, die Farbe kommt dazu
 * (WCAG 1.4.1). Der ganze Satz mit Haus und naechstem Schritt steht weiter darunter.
 */
const PILLE: Readonly<Record<TermKind, { readonly wort: string; readonly farbe: 'success' | 'warning' | 'informative' | 'subtle' }>> = {
  'proposal-other': { wort: 'Wartet auf deine Freigabe', farbe: 'warning' },
  // Dasselbe Wort wie im Rollenkatalog; wer freigibt, sagt der Satz darunter.
  'proposal-mine': { wort: 'Wartet auf Freigabe', farbe: 'warning' },
  active: { wort: 'Vergebbar', farbe: 'success' },
  groupwide: { wort: 'Für die ganze Gruppe', farbe: 'informative' },
  ended: { wort: 'Beendet', farbe: 'subtle' },
};

function ZustandsPille({
  art,
  wirksam = true,
  freigeben = true,
}: {
  readonly art: TermKind;
  readonly wirksam?: boolean;
  readonly freigeben?: boolean;
}): ReactElement {
  // Ein Hausstichwort ausserhalb seiner Gueltigkeit ist nicht vergebbar; die Pille sagt nichts anderes als der Satz.
  // "deine Freigabe" nur, wenn der Server sie dieser Person erlaubt.
  const { wort, farbe } =
    art === 'active' && !wirksam
      ? { wort: 'Gilt gerade nicht', farbe: 'subtle' as const }
      : art === 'proposal-other' && !freigeben
        ? { wort: 'Wartet auf Freigabe', farbe: 'warning' as const }
        : PILLE[art];
  return (
    <Badge appearance="tint" color={farbe}>
      {wort}
    </Badge>
  );
}

export interface StichwortkatalogScreenProps {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
}

export const STATUS_REGION_ID = 'stichwortkatalog-status';
/** Die gewaehlte Liste in der Adresse, damit Zurueck und Neuladen sie behalten. */
const LISTEN_PARAMETER = 'liste';
const LISTENWAHL_ID = 'stichwort-listenwahl';
export const SEARCH_STATUS_REGION_ID = 'stichwortsuche-status';

interface Rueckfrage {
  readonly id: string;
  /** Der Stand des Begriffs beim Oeffnen; aendert er sich beim Neuladen, gilt die Rueckfrage nicht mehr. */
  readonly lockVersion: number;
  /** Der Name beim Oeffnen; der Satz zum Schliessen nennt ihn auch, wenn der Begriff fehlt. */
  readonly name: string;
  readonly modus: 'beenden' | 'ersetzen';
  readonly grund: string;
  readonly ziel: string;
}

interface Eingabe {
  readonly taxonomyId: string;
  readonly operatingContextId: string;
  readonly displayName: string;
  readonly definition: string;
}

const LEER: Eingabe = { taxonomyId: '', operatingContextId: '', displayName: '', definition: '' };

const FELDSAETZE: Readonly<Record<string, string>> = Object.freeze({
  field_required: 'Bitte ausfüllen.',
  field_malformed: 'Bitte ohne Zeilenumbrüche und unsichtbare Zeichen eingeben.',
});

/**
 * Der Satz am Feld; „zu lang“ nennt Grenze und Ist-Wert, damit man sieht, wie viel weg muss.
 * Gezaehlt wird wie der Server (normalizeTermText, dann length): NFC, Leerraum zusammengezogen,
 * UTF-16-Einheiten. Sonst nennte der Satz bei einem Emoji eine Zahl unter der Grenze, die der
 * Server trotzdem ablehnt. Der Rollenkatalog zaehlt anders, weil sein Server Zeichen zaehlt.
 */
function feldSatz(feld: string, reasonCode: string, wert: string): string {
  if (reasonCode === 'field_too_long') {
    const grenze = feld === 'definition' ? MAXIMUM_TERM_DEFINITION_LENGTH : MAXIMUM_TERM_NAME_LENGTH;
    const laenge = wert.normalize('NFC').trim().replace(/\s+/gu, ' ').length;
    return `Höchstens ${grenze.toLocaleString('de-DE')} Zeichen. Jetzt sind es ${laenge.toLocaleString('de-DE')}.`;
  }
  return FELDSAETZE[reasonCode] ?? 'Bitte prüfen.';
}

/** Offen ist nur, was gespeichert sein kann; jede Antwort von LIAISON und jedes gescheiterte Laden ist ein Fehler. */
const OFFENE_MELDUNGEN: ReadonlySet<PlainMessage> = new Set([PROPOSAL_UNCERTAIN_MESSAGE, CHANGE_UNCERTAIN_MESSAGE, PREVIEW_UNCERTAIN_MESSAGE]);

function Meldung({ message }: { readonly message: PlainMessage }): ReactElement {
  return (
    <MessageBar intent={meldungsIntent(message, OFFENE_MELDUNGEN.has(message))}>
      <MessageBarBody>
        <MessageBarTitle>{message.headline}</MessageBarTitle>
        <div>{message.explanation}</div>
        <div>{message.nextStep}</div>
      </MessageBarBody>
    </MessageBar>
  );
}

export function StichwortkatalogScreen({ apiBaseUrl, accessToken, onReauthenticate }: StichwortkatalogScreenProps): ReactElement {
  const styles = useScreenStyles();
  const own = useStyles();
  const pflege = usePflegeStyles();
  const [status, setStatus] = useState<'laedt' | 'geladen' | 'abgelehnt'>('laedt');
  const [katalog, setKatalog] = useState<CatalogView | undefined>(undefined);
  const [ladefehler, setLadefehler] = useState<PlainMessage | undefined>(undefined);
  const [fehler, setFehler] = useState<PlainMessage | undefined>(undefined);
  const [umhaengenLaeuft, setUmhaengenLaeuft] = useState(false);
  const [bestaetigung, setBestaetigung] = useState<string | undefined>(undefined);
  /** Warum eine offene Rueckfrage beim Neuladen schloss; sonst verschwaende sie ohne Grund unter dem Finger. */
  const [geschlossen, setGeschlossen] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [eingabe, setEingabe] = useState<Eingabe>(LEER);
  // Das Formular zum Vorschlagen klappt erst auf Anforderung auf; ein leerer Katalog ist kein Grund, es offen zu zeigen.
  const anlegen = useAnlegen(false, 'stichwortkatalog');
  const [feldfehler, setFeldfehler] = useState<Readonly<Record<string, string>>>({});
  const [gehalten, setGehalten] = useState<HeldRequest | undefined>(undefined);
  const [geprueft, setGeprueft] = useState<ReadonlySet<string>>(new Set());
  const [offen, setOffen] = useState<Rueckfrage | undefined>(undefined);
  const [suchwort, setSuchwort] = useState('');
  const [suchfehler, setSuchfehler] = useState<string | undefined>(undefined);
  const [suchmeldung, setSuchmeldung] = useState<PlainMessage | undefined>(undefined);
  const [treffer, setTreffer] = useState<{ readonly hits: readonly CatalogHit[]; readonly truncated: boolean } | undefined>(undefined);
  const [sucht, setSucht] = useState(false);
  // Weitere Ziele fuer das Ersetzen, wenn der Katalog gekuerzt ist: Treffer einer Suche in der Rueckfrage.
  const [zielWort, setZielWort] = useState('');
  const [zielTreffer, setZielTreffer] = useState<readonly CatalogHit[]>([]);
  const [zielMeldung, setZielMeldung] = useState<string | undefined>(undefined);
  const [zielSucht, setZielSucht] = useState(false);
  const statusRef = useRef<HTMLDivElement | null>(null);
  /** Die Ansage nach der Wahl einer Liste; `mal` macht einen gleichen Satz erneut hoerbar. */
  const [listenAnsage, setListenAnsage] = useState<{ readonly satz: string; readonly mal: number } | undefined>(undefined);
  const listenParameter = useAdressParameter(LISTEN_PARAMETER);
  const [weitereLaedt, setWeitereLaedt] = useState(false);
  /** Was "Weitere laden" gebracht hat; `mal` macht einen gleichen Satz erneut hoerbar. */
  const [nachgeladen, setNachgeladen] = useState<{ readonly satz: string; readonly mal: number } | undefined>(undefined);
  const nachgeladenRef = useRef<HTMLSpanElement | null>(null);
  /** Hat die Person im Vorschlagsformular selbst eine Liste gewaehlt? Dann folgt es der Listenwahl nicht mehr. */
  const listeVonHand = useRef(false);
  const rueckfrageRef = useRef<HTMLDivElement | null>(null);
  /** Die offene Rueckfrage fuer `load`, das sonst mit dem Stand seines Aufrufs arbeitete. */
  const offenRef = useRef(offen);
  offenRef.current = offen;
  const [zurueckZu, setZurueckZu] = useState<string | undefined>(undefined);
  const [fokus, setFokus] = useState(0);
  const laufRef = useRef(0);
  // Eine Suchantwort gilt nur, solange nach ihr weder gesucht noch ein Befehl ausgefuehrt wurde.
  const suchLaufRef = useRef(0);

  useEffect(() => {
    if (fokus > 0) statusRef.current?.focus();
  }, [fokus]);

  // Die Rueckfrage ersetzt den ausloesenden Knopf; ohne Fokusfuehrung fiele
  // der Fokus auf den Seitenkoerper (WCAG 2.4.3). Beim Oeffnen geht er in die
  // Rueckfrage, beim Abbrechen zurueck auf den Knopf der Zeile.
  const offeneId = offen?.id;
  useEffect(() => {
    setZielWort('');
    setZielTreffer([]);
    setZielMeldung(undefined);
    if (offeneId !== undefined) rueckfrageRef.current?.querySelector<HTMLElement>('input, select, button')?.focus();
  }, [offeneId]);
  useEffect(() => {
    if (zurueckZu === undefined) return;
    document.getElementById(zurueckZu)?.focus();
    setZurueckZu(undefined);
  }, [zurueckZu]);

  const load = useCallback(async () => {
    const meinLauf = ++laufRef.current;
    setStatus((alt) => (alt === 'geladen' ? alt : 'laedt'));
    try {
      const token = await accessToken();
      if (meinLauf !== laufRef.current) return;
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const result = await fetchCatalog({ baseUrl: apiBaseUrl, accessToken: token });
      if (meinLauf !== laufRef.current) return;
      if (result.outcome === 'rejected') {
        setLadefehler(result.message);
        setStatus('abgelehnt');
        return;
      }
      setLadefehler(undefined);
      setKatalog(result.catalog);
      // Die offene Rueckfrage gilt nur fuer den Stand, auf dem sie geoeffnet wurde. Fehlt der Begriff
      // (neu geladen wird nur die erste Seite), ist er beendet oder hat er einen neuen Stand, schliesst
      // sie: sonst saendete Bestaetigen gegen einen Stand, den die Person nicht gesehen hat, und die
      // optimistische Sperre griffe nicht; ein beendeter Begriff liesse sie unsichtbar offen.
      const alt = offenRef.current;
      const jetzt = alt === undefined ? undefined : result.catalog.terms.find((term) => term.taxonomyTermId === alt.id);
      if (alt !== undefined && (jetzt === undefined || jetzt.endReasonCode !== null || jetzt.lockVersion !== alt.lockVersion || !jetzt.canEnd)) {
        // Lag der Fokus in der Rueckfrage, verschwaende er mit ihr: er geht in die Statusregion.
        const fokusDarin = rueckfrageRef.current?.contains(document.activeElement) === true;
        setOffen(undefined);
        setGeschlossen(
          jetzt === undefined
            ? `Die Rückfrage zu „${alt.name}“ ist geschlossen, weil es nicht mehr in der Liste steht.`
            : jetzt.endReasonCode !== null
              ? `Die Rückfrage zu „${alt.name}“ ist geschlossen, weil es inzwischen beendet ist.`
              : jetzt.lockVersion === alt.lockVersion
                ? `Die Rückfrage zu „${alt.name}“ ist geschlossen, weil du es nicht mehr ändern darfst.`
                : `Die Rückfrage zu „${alt.name}“ ist geschlossen, weil es inzwischen geändert wurde. Öffne sie erneut, wenn es dabei bleibt.`,
        );
        if (fokusDarin) setFokus((zahl) => zahl + 1);
      }
      // Der Katalog ist wieder die erste Seite; was "Weitere laden" gebracht hatte, steht nicht mehr da.
      setNachgeladen(undefined);
      // Eine einzige Wahl ist keine Wahl: Haus und Liste stehen dann fest. Eine
      // alte Wahl bleibt nur, wenn es sie im neuen Katalog noch gibt -- sonst
      // zeigte die Seite ein Haus und schickte ein anderes.
      setEingabe((alt) => ({
        ...alt,
        taxonomyId: gueltigeWahl(alt.taxonomyId, result.catalog.taxonomies.map((x) => x.taxonomyId)),
        operatingContextId: gueltigeWahl(alt.operatingContextId, result.catalog.houses.map((h) => h.operatingContextId)),
      }));
      setStatus('geladen');
    } catch {
      if (meinLauf !== laufRef.current) return;
      setLadefehler(LOAD_UNREACHABLE_MESSAGE);
      setStatus('abgelehnt');
    }
  }, [accessToken, apiBaseUrl, onReauthenticate]);

  useEffect(() => {
    void load();
    return () => {
      laufRef.current += 1;
    };
  }, [load]);

  const weitereLaden = async (): Promise<void> => {
    const cursor = katalog?.nextCursor;
    if (cursor === undefined || weitereLaedt) return;
    const meinLauf = laufRef.current;
    setWeitereLaedt(true);
    setFehler(undefined);
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const result = await fetchCatalog({ baseUrl: apiBaseUrl, accessToken: token, cursor });
      // Ein Neuladen dazwischen hat den Katalog ersetzt; diese Seite gehoert nicht mehr dazu.
      if (meinLauf !== laufRef.current) return;
      if (result.outcome === 'rejected') {
        setFehler(result.message);
        setFokus((zahl) => zahl + 1);
        return;
      }
      const zusammen = katalog === undefined ? { catalog: result.catalog, added: result.catalog.terms.length } : appendCatalogPage(katalog, result.catalog);
      setKatalog(zusammen.catalog);
      setNachgeladen((alt) => ({ satz: morePagesSentence(zusammen.added), mal: (alt?.mal ?? 0) + 1 }));
    } catch {
      if (meinLauf !== laufRef.current) return;
      setFehler(MORE_UNREACHABLE_MESSAGE);
      setFokus((zahl) => zahl + 1);
    } finally {
      setWeitereLaedt(false);
    }
  };
  // War es die letzte Seite, verschwindet "Weitere laden"; der Fokus faellt dann nicht auf BODY,
  // sondern auf die Ansage im Fuss. Sonst bleibt er auf dem Knopf.
  const nochMehr = katalog?.nextCursor !== undefined;
  useEffect(() => {
    if (nachgeladen !== undefined && !nochMehr && document.activeElement === document.body) nachgeladenRef.current?.focus();
  }, [nachgeladen, nochMehr]);

  /** Fuehrt einen Befehl aus, meldet das Ergebnis und laedt neu. */
  const ausfuehren = async (
    run: (token: string) => Promise<TermCommandResult>,
    erfolg: string,
    feldweise?: (errors: readonly FieldError[]) => void,
    offen: PlainMessage = CHANGE_UNCERTAIN_MESSAGE,
  ): Promise<TermCommandResult | undefined> => {
    setBusy(true);
    setFehler(undefined);
    setBestaetigung(undefined);
    setGeschlossen(undefined);
    setListenAnsage(undefined);
    // Jede Bestaetigung gilt fuer genau einen Versuch; nach einem Befehl und
    // dem Neuladen ist sie neu zu geben.
    setGeprueft(new Set());
    let result: TermCommandResult | undefined;
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return undefined;
      }
      result = await run(token);
      if (result.outcome === 'applied') {
        setBestaetigung(erfolg);
        // Ein Treffer von vorher koennte das Ersetzte noch zeigen.
        suchLaufRef.current += 1;
        setTreffer(undefined);
        setSucht(false);
      } else if (result.outcome === 'invalid') {
        feldweise?.(result.errors);
        setFehler({
          headline: 'Bitte prüfe die markierten Felder.',
          explanation: 'Es wurde nichts gespeichert.',
          nextStep: 'Korrigiere die Eingabe und versuche es erneut.',
          retryable: false,
        });
      } else {
        setFehler(result.message);
      }
      await load();
    } catch {
      setFehler(offen);
    } finally {
      setBusy(false);
      setFokus((zahl) => zahl + 1);
    }
    return result;
  };

  const vorschlagen = async (): Promise<void> => {
    const fehlend: Record<string, string> = {};
    for (const feld of ['taxonomyId', 'operatingContextId', 'displayName', 'definition'] as const) {
      if (eingabe[feld].trim() === '') fehlend[feld] = FELDSAETZE.field_required!;
    }
    setFeldfehler(fehlend);
    if (Object.keys(fehlend).length > 0) {
      setFehler(undefined);
      setBestaetigung(undefined);
      setGeschlossen(undefined);
      setListenAnsage(undefined);
      setFokus((zahl) => zahl + 1);
      setFehler({
        headline: 'Bitte fülle alle Felder aus.',
        explanation: 'Ein Stichwort braucht einen Namen und eine Beschreibung, wann es vergeben wird.',
        nextStep: 'Ergänze die markierten Felder.',
        retryable: false,
      });
      return;
    }
    const halten = requestFor(gehalten, requestKeyInput({ ...eingabe }));
    setGehalten(halten);
    const name = eingabe.displayName.trim();
    let result: TermCommandResult | undefined;
    try {
      result = await ausfuehren(
        (token) => proposeTerm({ baseUrl: apiBaseUrl, accessToken: token, input: eingabe, clientRequestId: halten.id }),
        appliedSentence('propose', name),
        (errors) =>
          setFeldfehler(
            Object.fromEntries(
              errors.map((e) => [e.fieldPath, feldSatz(e.fieldPath, e.reasonCode, e.fieldPath === 'definition' ? eingabe.definition.trim() : name)]),
            ),
          ),
        PROPOSAL_UNCERTAIN_MESSAGE,
      );
    } finally {
      // Die Kennung bleibt nur, wenn eine Wiederholung etwas aendern kann
      // (Abbruch oder voruebergehende Stoerung); sonst ist die naechste
      // Sendung eine neue.
      if (result !== undefined && !keepsRequest(result)) setGehalten(undefined);
    }
    if (result?.outcome === 'applied') {
      setEingabe((alt) => ({ ...alt, displayName: '', definition: '' }));
      anlegen.erledigt();
      setFeldfehler({});
    }
  };

  const freigeben = (term: CatalogTermView): void => {
    void ausfuehren(
      (token) => approveProposal({ baseUrl: apiBaseUrl, accessToken: token, term }),
      appliedSentence('approve', term.displayName),
    );
  };

  const beenden = (term: CatalogTermView, grund: string): void => {
    setOffen(undefined);
    void ausfuehren(
      (token) => endCatalogTerm({ baseUrl: apiBaseUrl, accessToken: token, term, endReasonCode: grund }),
      appliedSentence(term.termStatus === 'draft' ? (term.createdByMe ? 'withdraw' : 'discard') : 'end', term.displayName),
    );
  };

  const umhaengen = async (term: CatalogTermView): Promise<void> => {
    setBusy(true);
    setUmhaengenLaeuft(true);
    setFehler(undefined);
    setBestaetigung(undefined);
    setGeschlossen(undefined);
    setListenAnsage(undefined);
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const result = await createMergeBatch({ baseUrl: apiBaseUrl, accessToken: token, taxonomyTermId: term.taxonomyTermId });
      if (result.outcome === 'applied') {
        navigate(massenlaufHref(result.classificationChangeBatchId));
        return;
      }
      setFehler(result.message);
    } catch {
      setFehler(PREVIEW_UNCERTAIN_MESSAGE);
    } finally {
      setBusy(false);
      setUmhaengenLaeuft(false);
      setFokus((zahl) => zahl + 1);
    }
  };

  const abbrechen = (knopfId: string): void => {
    setOffen(undefined);
    setZurueckZu(knopfId);
  };

  const ersetzen = (term: CatalogTermView, ziel: { readonly taxonomyTermId: string; readonly displayName: string }): void => {
    setOffen(undefined);
    void ausfuehren(
      (token) => replaceCatalogTerm({ baseUrl: apiBaseUrl, accessToken: token, term, replacementTermId: ziel.taxonomyTermId }),
      appliedSentence('replace', term.displayName, ziel.displayName),
    );
  };

  /** Sucht weitere Ziele; angeboten wird davon nur, was replacementCandidates nach den Regeln des Servers zulaesst. */
  const zielSuchen = async (term: CatalogTermView): Promise<void> => {
    const wort = zielWort.normalize('NFC').trim().replace(/\s+/gu, ' ');
    if (wort.length < MINIMUM_SEARCH_LENGTH) {
      setZielMeldung(`Bitte mindestens ${MINIMUM_SEARCH_LENGTH} Zeichen eingeben.`);
      return;
    }
    setZielSucht(true);
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const result = await searchCatalog({ baseUrl: apiBaseUrl, accessToken: token, query: wort });
      if (result.outcome !== 'applied') {
        setZielMeldung('Die Suche ist gerade nicht möglich. Versuche es noch einmal.');
        return;
      }
      setZielTreffer((alt) => [...alt, ...result.hits]);
      const passend = replacementCandidates(term, [], result.hits).length;
      setZielMeldung(
        passend === 0
          ? 'Dazu gibt es kein Stichwort, das als Ersatz in Frage kommt.'
          : `${passend === 1 ? 'Ein passendes Stichwort' : `${passend} passende Stichworte`} zur Auswahl hinzugefügt.`,
      );
    } catch {
      setZielMeldung('Die Suche ist gerade nicht möglich. Versuche es noch einmal.');
    } finally {
      setZielSucht(false);
    }
  };

  const suchen = async (): Promise<void> => {
    const wort = suchwort.normalize('NFC').trim().replace(/\s+/gu, ' ');
    setSuchmeldung(undefined);
    if (wort.length < MINIMUM_SEARCH_LENGTH) {
      setSuchfehler(`Bitte mindestens ${MINIMUM_SEARCH_LENGTH} Zeichen eingeben.`);
      setTreffer(undefined);
      return;
    }
    setSuchfehler(undefined);
    setSucht(true);
    const meinLauf = ++suchLaufRef.current;
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const result = await searchCatalog({ baseUrl: apiBaseUrl, accessToken: token, query: wort });
      if (meinLauf !== suchLaufRef.current) return;
      if (result.outcome === 'applied') {
        setTreffer({ hits: result.hits, truncated: result.truncated });
      } else if (result.outcome === 'invalid') {
        setTreffer(undefined);
        setSuchfehler('Bitte ohne Zeilenumbrüche und unsichtbare Zeichen suchen, höchstens ' + MAXIMUM_TERM_NAME_LENGTH + ' Zeichen.');
      } else {
        setTreffer(undefined);
        setSuchmeldung(result.message);
      }
    } catch {
      if (meinLauf !== suchLaufRef.current) return;
      setTreffer(undefined);
      setSuchmeldung(SEARCH_UNREACHABLE_MESSAGE);
    } finally {
      if (meinLauf === suchLaufRef.current) setSucht(false);
    }
  };

  const hausname = (id: string | null): string | undefined => katalog?.houses.find((h) => h.operatingContextId === id)?.displayName;
  const setze = (feld: keyof Eingabe) => (wert: string) => {
    if (feld === 'taxonomyId') listeVonHand.current = true;
    setEingabe((alt) => ({ ...alt, [feld]: wert }));
  };

  const { zeilenknopf } = pflege;
  const zeile = (term: CatalogTermView, ohneRecht = false): ReactElement => {
    const art = termKind(term);
    // Ein Entwurf wird verworfen, ein freigegebenes Stichwort beendet; wer was darf, sagt canEnd.
    // Den eigenen Vorschlag zieht man zurueck, wie im Rollenkatalog (Glossar Abschnitt 3).
    const entwurf = term.termStatus === 'draft';
    const endeWort = entwurf ? (art === 'proposal-mine' ? 'Zurückziehen' : 'Verwerfen') : 'Beenden';
    const pruefId = `stichwort-pruefung-${term.taxonomyTermId}`;
    const hinweisId = `stichwort-hinweis-${term.taxonomyTermId}`;
    const rueckfrage = offen?.id === term.taxonomyTermId;
    const kandidaten = rueckfrage && offen.modus === 'ersetzen' ? replacementCandidates(term, katalog?.terms ?? [], zielTreffer) : [];
    const zielName = rueckfrage ? kandidaten.find((k) => k.taxonomyTermId === offen.ziel)?.displayName : undefined;
    return (
      <li
        key={term.taxonomyTermId}
        className={[own.eintrag, art === 'ended' ? own.beendet : ''].join(' ')}
      >
        <div className={own.text}>
        <div className={own.namenszeile}>
          <Body1>
            <strong>{term.displayName}</strong>
          </Body1>
          <ZustandsPille art={art} wirksam={term.inEffect} freigeben={term.canApprove} />
        </div>
        {term.definition !== null ? <Body1 className={own.satz}>{term.definition}</Body1> : undefined}
        {/* "Für die ganze Gruppe" erklaert der Satz unter der Listenueberschrift EINMAL, nicht jede Zeile. */}
        {art === 'groupwide' ? undefined : (
          <Caption1 id={hinweisId} className={own.satz}>
            {termStatusText(term, hausname(term.operatingContextId), ohneRecht)}
          </Caption1>
        )}
        </div>
        {/* Die Zeilenknoepfe vor der Freigabezeile: so stehen sie rechts neben dem Text wie in jeder
            Zeile, und die Tab-Reihenfolge folgt dem Bild (Restliste B4). */}
        {term.canEnd && !rueckfrage ? (
          <div className={own.aktionen}>
            {/* Wie die Zeilenaktionen der Pflegeseiten: kleine Umrissknoepfe, Beenden (aendert den Bestand) zuletzt. */}
            {term.termStatus === 'active' ? (
              <Button
                id={`stichwort-ersetzen-${term.taxonomyTermId}`}
                appearance="secondary"
                size="small"
                className={zeilenknopf}
                disabled={busy}
                onClick={() => setOffen({ id: term.taxonomyTermId, lockVersion: term.lockVersion, name: term.displayName, modus: 'ersetzen', grund: '', ziel: '' })}
                aria-label={`Ersetzen durch …: ${term.displayName}`}
              >
                Ersetzen durch …
              </Button>
            ) : undefined}
            <Button
              id={`stichwort-aktion-${term.taxonomyTermId}`}
              appearance="secondary"
              size="small"
              className={zeilenknopf}
              disabled={busy}
              onClick={() =>
                setOffen({
                  id: term.taxonomyTermId,
                  lockVersion: term.lockVersion,
                  name: term.displayName,
                  modus: 'beenden',
                  grund: entwurf ? 'created_in_error' : 'obsolete',
                  ziel: '',
                })
              }
              aria-label={`${endeWort}: ${term.displayName}`}
            >
              {endeWort}
            </Button>
          </div>
        ) : undefined}
        {term.canApprove ? (
          // Pruefsatz und Freigabe brauchen die volle Breite; in der rechten Spalte waere der Satz gequetscht.
          <div className={`${own.aktionen} ${own.voll}`}>
            <Checkbox
              id={pruefId}
              checked={geprueft.has(term.taxonomyTermId)}
              onChange={(_event, data) =>
                setGeprueft((alt) => {
                  const neu = new Set(alt);
                  if (data.checked === true) neu.add(term.taxonomyTermId);
                  else neu.delete(term.taxonomyTermId);
                  return neu;
                })
              }
              label={`„${term.displayName}“ beschreibt ein Sortiment oder Thema, kein Merkmal einer Person`}
            />
            <Button
              appearance="secondary"
              size="small"
              className={zeilenknopf}
              disabled={busy || !geprueft.has(term.taxonomyTermId)}
              onClick={() => freigeben(term)}
              aria-label={`Freigeben: ${term.displayName}`}
              aria-describedby={hinweisId}
            >
              Freigeben
            </Button>
          </div>
        ) : undefined}
        {art === 'ended' && term.endReasonCode === 'replaced' && term.operatingContextId !== null ? (
          <div className={`${own.aktionen} ${own.voll}`}>
            <Button
              id={`stichwort-umhaengen-${term.taxonomyTermId}`}
              appearance="secondary"
              size="small"
              className={zeilenknopf}
              disabled={busy}
              onClick={() => void umhaengen(term)}
              aria-label={`Umhängen vorbereiten: ${term.displayName}`}
              aria-describedby={`${hinweisId} stichwort-umhaengen-hinweis-${term.taxonomyTermId}`}
            >
              Umhängen vorbereiten
            </Button>
            <Caption1 id={`stichwort-umhaengen-hinweis-${term.taxonomyTermId}`} className={own.satz}>
              Du siehst zuerst, wie viele Kontakte es betrifft; geändert wird erst nach deiner Bestätigung.
            </Caption1>
          </div>
        ) : undefined}
        {rueckfrage && offen.modus === 'beenden' ? (
          <div
            className={`${own.aktionen} ${own.voll}`}
            role="group"
            aria-label={`Rückfrage: ${term.displayName}`}
            ref={rueckfrageRef}
            // Esc wie Abbrechen, nicht waehrend der Server antwortet.
            onKeyDown={(event) => (busy ? undefined : beiEscapeSchliessen(event, () => abbrechen(`stichwort-aktion-${term.taxonomyTermId}`)))}
          >
            {!entwurf ? (
              <Field label="Warum wird das Stichwort beendet?">
                <RadioGroup value={offen.grund} onChange={(_event, data) => setOffen({ ...offen, grund: data.value })}>
                  {END_REASONS.map((grund) => (
                    <Radio key={grund.code} value={grund.code} label={grund.label} />
                  ))}
                </RadioGroup>
              </Field>
            ) : undefined}
            {!entwurf ? (
              <Body1>Kontakte, die es tragen, behalten es; neu vergeben lässt es sich danach nicht mehr.</Body1>
            ) : (
              <Body1>
                {art === 'proposal-mine'
                  ? 'Deinen Vorschlag zurückziehen? Er wird nicht freigegeben und lässt sich nicht vergeben.'
                  : 'Den Vorschlag verwerfen? Er wird als versehentlich angelegt beendet.'}
              </Body1>
            )}
            <Button appearance="secondary" disabled={busy} onClick={() => beenden(term, offen.grund)}>
              {`${endeWort} bestätigen`}
            </Button>
            <NebenKnopf disabled={busy} onClick={() => abbrechen(`stichwort-aktion-${term.taxonomyTermId}`)}>
              Abbrechen
            </NebenKnopf>
          </div>
        ) : undefined}
        {rueckfrage && offen.modus === 'ersetzen' ? (
          <div
            className={`${own.aktionen} ${own.rueckfrageSpalte} ${own.voll}`}
            role="group"
            aria-label={`Rückfrage: ${term.displayName} ersetzen`}
            ref={rueckfrageRef}
            onKeyDown={(event) => (busy ? undefined : beiEscapeSchliessen(event, () => abbrechen(`stichwort-ersetzen-${term.taxonomyTermId}`)))}
          >
            {katalog?.nextCursor !== undefined ? (
              <>
                <Field
                  label="Weiteres Stichwort suchen"
                  hint="Der Katalog zeigt nicht alle Stichworte. Suche das neue Stichwort nach Name; passende kommen in die Auswahl."
                >
                  <Input
                    disabled={busy}
                    value={zielWort}
                    maxLength={MAXIMUM_TERM_NAME_LENGTH}
                    onChange={(_event, data) => setZielWort(data.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        void zielSuchen(term);
                      }
                    }}
                  />
                </Field>
                <Button appearance="secondary" disabled={busy || zielSucht} onClick={() => void zielSuchen(term)}>
                  Stichwort suchen
                </Button>
                <div aria-live="polite" role="status" aria-label={`Status: Suche nach einem Ersatz für ${term.displayName}`}>
                  {zielMeldung !== undefined ? <Body1>{zielMeldung}</Body1> : undefined}
                </div>
              </>
            ) : undefined}
            {kandidaten.length === 0 ? (
              <Body1>
                Zum Ersetzen braucht es ein zweites freigegebenes Stichwort in dieser Liste und diesem Verlag. Schlage eines vor oder
                lass einen Vorschlag freigeben.
              </Body1>
            ) : (
              <>
                <Field label={`Wodurch wird „${term.displayName}“ ersetzt?`} required>
                  <Select disabled={busy} value={offen.ziel} onChange={(_event, data) => setOffen({ ...offen, ziel: data.value })}>
                    <option value="">Bitte wählen</option>
                    {kandidaten.map((ziel) => (
                      <option key={ziel.taxonomyTermId} value={ziel.taxonomyTermId}>
                        {ziel.displayName}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Body1>
                  {zielName !== undefined ? `„${term.displayName}“ wird durch „${zielName}“ ersetzt. ` : ''}„{term.displayName}“ lässt sich danach
                  nicht mehr vergeben. Kontakte, die es tragen, behalten es. Wer hier im Katalog nach dem alten Namen sucht, findet das neue
                  Stichwort.
                </Body1>
                <Button appearance="secondary" disabled={busy || zielName === undefined} onClick={() => ersetzen(term, kandidaten.find((k) => k.taxonomyTermId === offen.ziel)!)}>
                  {zielName !== undefined ? `Ersetzen durch „${zielName}“ bestätigen` : 'Ersetzen bestätigen'}
                </Button>
              </>
            )}
            <NebenKnopf disabled={busy} onClick={() => abbrechen(`stichwort-ersetzen-${term.taxonomyTermId}`)}>
              Abbrechen
            </NebenKnopf>
          </div>
        ) : undefined}
      </li>
    );
  };

  const haeuser = katalog?.houses ?? [];
  const listen = katalog?.taxonomies ?? [];
  const kannVorschlagen = haeuser.length > 0 && listen.length > 0;
  // Erst ab zwei Listen gibt es eine Wahl; eine unbekannte Liste in der Adresse heisst still "Alle Listen".
  const gewaehlteListe = listen.length > 1 ? listen.find((liste) => liste.taxonomyId === listenParameter) : undefined;
  const gezeigteListen = gewaehlteListe === undefined ? listen : [gewaehlteListe];
  // Solange weitere Seiten ausstehen, ist die Zahl, was unter der Ueberschrift steht, und "mehr"
  // sagt, dass es eine Untergrenze ist.
  const mehrStichworte = katalog?.nextCursor !== undefined;
  const begriffeVon = (taxonomyId: string): readonly CatalogTermView[] => (katalog?.terms ?? []).filter((term) => term.taxonomyId === taxonomyId);
  const gezeigteTreffer = treffer === undefined || gewaehlteListe === undefined ? treffer?.hits : treffer.hits.filter((hit) => hit.taxonomyId === gewaehlteListe.taxonomyId);
  const trefferAnderswo = treffer === undefined || gezeigteTreffer === undefined ? 0 : treffer.hits.length - gezeigteTreffer.length;
  // Ist eine Liste gewaehlt, schlaegt man meist fuer sie vor: das Formular folgt der Wahl, bis die
  // Person dort selbst eine Liste waehlt (dann gilt ihre Wahl). Bei "Alle Listen" bleibt es leer statt
  // bei der frueheren Wahl, sonst landet ein Vorschlag leicht in der falschen Liste (A7 V3).
  const gewaehlteListenId = gewaehlteListe?.taxonomyId;
  const mehrereListen = listen.length > 1;
  useEffect(() => {
    if (mehrereListen && !listeVonHand.current) setEingabe((alt) => ({ ...alt, taxonomyId: gewaehlteListenId ?? '' }));
  }, [gewaehlteListenId, mehrereListen]);
  const listeWaehlen = (taxonomyId: string): void => {
    const liste = listen.find((kandidat) => kandidat.taxonomyId === taxonomyId);
    setzeAdressParameter(LISTEN_PARAMETER, liste?.taxonomyId, true);
    const zahl = liste === undefined ? (katalog?.terms.length ?? 0) : begriffeVon(liste.taxonomyId).length;
    setListenAnsage((alt) => ({ satz: listChosenSentence(liste?.displayName, zahl, mehrStichworte), mal: (alt?.mal ?? 0) + 1 }));
  };

  return (
    <Card className={styles.card}>
      {/* Kein Einleitungssatz: wie ein Vorschlag freigegeben wird, sagt das Formular, wo es gebraucht wird;
          was "Für die ganze Gruppe" heisst, sagt jede Liste einmal. */}
      <div aria-live="polite" role="status" aria-label="Status: Stichwortkatalog" id={STATUS_REGION_ID} ref={statusRef} tabIndex={-1} className={own.hinweis}>
        {status === 'laedt' ? <Spinner labelPosition="after" label="Stichwortkatalog wird geladen" /> : undefined}
        {umhaengenLaeuft ? <Spinner labelPosition="after" label="Vorschau wird vorbereitet" /> : undefined}
        {bestaetigung !== undefined ? (
          <MessageBar intent="success">
            <MessageBarBody>
              <div>{bestaetigung}</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
        {fehler !== undefined ? <Meldung message={fehler} /> : undefined}
        {geschlossen !== undefined ? (
          <MessageBar intent="info">
            <MessageBarBody>
              <div>{geschlossen}</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
        {ladefehler !== undefined ? <Meldung message={ladefehler} /> : undefined}
        {katalog?.housesTruncated === true ? (
          <Caption1>Du hast Zugriff auf mehr Gesellschaften der Gruppe, als hier zur Auswahl stehen. Wende dich an den Betrieb.</Caption1>
        ) : undefined}
      </div>
      <VorgangNach meldungen={[fehler, ladefehler]} />
      {status === 'geladen' && fehler?.retryable === true ? (
        <div className={styles.actions}>
          <Button appearance="secondary" disabled={busy} onClick={() => void load()}>
            Katalog neu laden
          </Button>
        </div>
      ) : undefined}
      {status === 'abgelehnt' && ladefehler?.retryable === true ? (
        <div className={styles.actions}>
          <Button appearance="secondary" onClick={() => void load()}>
            Erneut versuchen
          </Button>
        </div>
      ) : undefined}
      {status === 'geladen' && katalog !== undefined ? (
        <>
          {/* Zuerst, welche Liste man pflegt (A7 V1, V2): dasselbe Wort wie im Formular, und auch eine
              einzige Liste ist genannt. Der Satz darunter sagt, wohin ein neuer Vorschlag geht -- und
              entfaellt, wenn im offenen Formular von Hand eine andere Liste steht: dann steht die Wahl dort
              sichtbar, und der Satz stimmte nicht mehr (DESIGN zu Codex-P2 an #739). */}
          {listen.length > 1 ? (
            <Field
              label="Stichwortliste"
              className={own.listenwahl}
              hint={
                gewaehlteListe === undefined
                  ? 'Alle Listen untereinander. Für einen Vorschlag wählst du die Liste im Formular.'
                  : !anlegen.offen || eingabe.taxonomyId === gewaehlteListe.taxonomyId
                    ? 'Neue Vorschläge gehen in diese Liste.'
                    : null
              }
            >
              <Select id={LISTENWAHL_ID} className={own.listenfeld} value={gewaehlteListe?.taxonomyId ?? ''} onChange={(_event, data) => listeWaehlen(data.value)}>
                <option value="">Alle Listen</option>
                {listen.map((liste) => (
                  <option key={liste.taxonomyId} value={liste.taxonomyId}>
                    {liste.displayName}
                  </option>
                ))}
              </Select>
            </Field>
          ) : listen.length === 1 ? (
            <Body1>Stichwortliste: {listen[0]!.displayName}</Body1>
          ) : undefined}
          {/* Darunter die Arbeit an der Liste (v4): Suche als Filterzeile, daneben "Stichwort vorschlagen"
              als Hauptaktion; das Formular erscheint erst auf Anforderung (5.2 progressive Offenlegung). */}
          <div className={own.werkzeug}>
            <form
              className={own.suchform}
              noValidate
              role="search"
              aria-label="Stichwort suchen"
              onSubmit={(event) => {
                event.preventDefault();
                void suchen();
              }}
            >
              {/* Ein Filterfeld mit Lupe (v4, wie /kontakte): Enter oder die Lupe suchen, kein eigener Knopf.
                  Die Mindestlaenge sagt erst der Feldsatz, wenn sie verfehlt ist. */}
              <Field label="Stichwort suchen" validationMessage={suchfehler ?? null} className={own.suchfeld}>
                <Input
                  value={suchwort}
                  maxLength={MAXIMUM_TERM_NAME_LENGTH}
                  placeholder="Name oder früherer Name"
                  contentAfter={
                    <Button type="submit" appearance="transparent" size="small" icon={<Symbol name="suche" />} aria-label="Suchen" disabled={sucht} className={own.lupe} />
                  }
                  onChange={(_event, data) => {
                    setSuchwort(data.value);
                    setSuchfehler(undefined);
                    // Ein geleertes Filterfeld nimmt das Ergebnis mit, damit die Listen wieder oben stehen.
                    if (data.value.trim() === '') {
                      suchLaufRef.current += 1;
                      setTreffer(undefined);
                      setSuchmeldung(undefined);
                      setSucht(false);
                    }
                  }}
                />
              </Field>
            </form>
            {kannVorschlagen ? (
              <AnlegenOeffner steuerung={anlegen} label="Stichwort vorschlagen" disabled={busy} zuruecktreten={false} />
            ) : undefined}
          </div>
            <div aria-live="polite" role="status" aria-label="Status: Stichwortsuche" id={SEARCH_STATUS_REGION_ID} className={own.hinweis}>
              {sucht ? <Spinner labelPosition="after" label="Es wird gesucht" /> : undefined}
              {suchmeldung !== undefined ? <Meldung message={suchmeldung} /> : undefined}
              {treffer !== undefined && gezeigteTreffer !== undefined ? (
                <Body1 className={own.satzZeile}>
                  {listSearchSummary(gezeigteTreffer.length, treffer.truncated, trefferAnderswo, gewaehlteListe !== undefined)}
                </Body1>
              ) : undefined}
              {/* Treffer anderer Listen nicht still verschlucken (DESIGN L3): der Satz in der Region, damit er
                  angesagt wird; der Weg zu allen daneben. */}
              {trefferAnderswo > 0 ? <Body1 className={own.satzZeile}>{otherListsSentence(trefferAnderswo)}</Body1> : undefined}
            </div>
            {trefferAnderswo > 0 ? (
              <div className={own.anderswo}>
                <Button
                  appearance="secondary"
                  onClick={() => {
                    listeWaehlen('');
                    // Der Knopf verschwindet mit der Wahl; der Fokus geht an die Auswahl, nicht auf BODY.
                    document.getElementById(LISTENWAHL_ID)?.focus();
                  }}
                >
                  In allen Listen zeigen
                </Button>
              </div>
            ) : undefined}
            <VorgangNach meldungen={[suchmeldung]} />
            {gezeigteTreffer !== undefined && gezeigteTreffer.length > 0 ? (
              <ul className={own.liste} aria-label="Suchergebnis">
                {gezeigteTreffer.map((treffe) => (
                  <li key={treffe.taxonomyTermId} className={[own.eintrag, treffe.endReasonCode !== null ? own.beendet : ''].join(' ')}>
                    <div className={own.text}>
                    <div className={own.namenszeile}>
                      <Body1>
                        <strong>{treffe.displayName}</strong>
                      </Body1>
                      {treffe.endReasonCode !== null ? <ZustandsPille art="ended" /> : undefined}
                    </div>
                    {treffe.formerName !== null ? (
                      <Body1 className={own.satz}>Gefunden unter dem früheren Namen „{treffe.formerName}“.</Body1>
                    ) : undefined}
                    {treffe.definition !== null ? <Body1 className={own.satz}>{treffe.definition}</Body1> : undefined}
                    <Caption1 className={own.satz}>{hitStatusText(treffe, hausname(treffe.operatingContextId))}</Caption1>
                    </div>
                  </li>
                ))}
              </ul>
            ) : undefined}
          {kannVorschlagen ? (
            <AnlegenFormular
              steuerung={anlegen}
              name="Stichwort vorschlagen"
              className={own.formular}
              onSenden={() => void vorschlagen()}
              absenden={
                // Waehrend der Sendung ist das Formular gesperrt: sonst leerte der Erfolg eine inzwischen
                // begonnene naechste Eingabe.
                <Button type="submit" appearance="primary" disabled={busy}>
                  Vorschlagen
                </Button>
              }
            >
              <Body1>Ein neues Stichwort ist zuerst ein Vorschlag; eine andere Person aus deinem Verlag gibt es frei.</Body1>
              {haeuser.length > 1 ? (
                <Field label="Verlag" required validationMessage={feldfehler.operatingContextId ?? null}>
                  <Select disabled={busy} value={eingabe.operatingContextId} onChange={(_event, data) => setze('operatingContextId')(data.value)}>
                    <option value="">Bitte wählen</option>
                    {haeuser.map((haus) => (
                      <option key={haus.operatingContextId} value={haus.operatingContextId}>
                        {haus.displayName}
                      </option>
                    ))}
                  </Select>
                </Field>
              ) : (
                <Body1>Für: {haeuser[0]!.displayName}</Body1>
              )}
              {listen.length > 1 ? (
                <Field label="Stichwortliste" required validationMessage={feldfehler.taxonomyId ?? null}>
                  <Select disabled={busy} value={eingabe.taxonomyId} onChange={(_event, data) => setze('taxonomyId')(data.value)}>
                    <option value="">Bitte wählen</option>
                    {listen.map((liste) => (
                      <option key={liste.taxonomyId} value={liste.taxonomyId}>
                        {liste.displayName}
                      </option>
                    ))}
                  </Select>
                </Field>
              ) : (
                <Body1>Stichwortliste: {listen[0]!.displayName}</Body1>
              )}
              <Field label="Name" required validationMessage={feldfehler.displayName ?? null} hint={`Höchstens ${MAXIMUM_TERM_NAME_LENGTH} Zeichen.`}>
                <Input disabled={busy} value={eingabe.displayName} maxLength={MAXIMUM_TERM_NAME_LENGTH} onChange={(_event, data) => setze('displayName')(data.value)} />
              </Field>
              <Field
                label="Wann wird es vergeben?"
                required
                validationMessage={feldfehler.definition ?? null}
                hint="Ein Sortiment oder Thema, zum Beispiel „Krimis mit Schauplatz in Norddeutschland“ — nie ein Merkmal einer Person."
              >
                <Textarea disabled={busy} value={eingabe.definition} maxLength={MAXIMUM_TERM_DEFINITION_LENGTH} onChange={(_event, data) => setze('definition')(data.value)} />
              </Field>
            </AnlegenFormular>
          ) : (
            <Body1 className={own.satz}>
              Du kannst hier gerade kein Stichwort vorschlagen: dir ist kein Verlag mit einer pflegbaren Stichwortliste zugeordnet.
              Wende dich an den Betrieb, wenn du Stichworte deines Verlags pflegen sollst.
            </Body1>
          )}
          {gezeigteListen.map((liste) => {
            // Was auf die Lesende wartet, steht oben; Beendetes unten.
            const rang = { 'proposal-other': 0, 'proposal-mine': 1, active: 2, groupwide: 2, ended: 3 } as const;
            const begriffe = begriffeVon(liste.taxonomyId)
              .map((term, index) => ({ term, index }))
              .sort((a, b) => rang[termKind(a.term)] - rang[termKind(b.term)] || a.index - b.index)
              .map(({ term }) => term);
            return (
              <section key={liste.taxonomyId} className={own.abschnitt} aria-labelledby={`liste-${liste.taxonomyId}`}>
                <Subtitle2
                  as="h2"
                  id={`liste-${liste.taxonomyId}`}
                  {...(mehrStichworte ? { 'aria-label': listHeading(liste.displayName, begriffe.length, true).spoken } : {})}
                >
                  {listHeading(liste.displayName, begriffe.length, mehrStichworte).text}
                </Subtitle2>
                {begriffe.some((term) => termKind(term) === 'groupwide') ? (
                  <Caption1 className={own.gruppenSatz}>{GROUPWIDE_NOTE}</Caption1>
                ) : undefined}
                {needsStewardNote(begriffe) ? <Caption1 className={own.gruppenSatz}>{STEWARD_CHANGE_NOTE}</Caption1> : undefined}
                {begriffe.length === 0 ? (
                  <Body1 className={own.satz}>Diese Liste hat noch keine Stichworte.</Body1>
                ) : (
                  <ul className={own.liste} aria-label={`Stichworte: ${liste.displayName}`}>
                    {begriffe.map((term) => zeile(term, needsStewardNote(begriffe)))}
                  </ul>
                )}
              </section>
            );
          })}
          {/* Fuss unter allen Listen (Muster Rollenkatalog): die naechste Seite gilt fuer alle Listen. */}
          <div className={own.fuss}>
            {nochMehr ? (
              <Button appearance="secondary" disabledFocusable={weitereLaedt} onClick={() => void weitereLaden()}>
                {weitereLaedt ? 'Wird geladen …' : 'Weitere laden'}
              </Button>
            ) : undefined}
            {/* Im Baum ab dem ersten Bild, sonst ginge die erste Ansage verloren. */}
            <span ref={nachgeladenRef} tabIndex={-1} role="status" aria-label="Status: Weitere Stichworte" aria-live="polite">
              {nachgeladen === undefined ? undefined : <span key={nachgeladen.mal}>{nachgeladen.satz}</span>}
            </span>
          </div>
        </>
      ) : undefined}
      {/* Am Ende der Karte: im Baum ab dem ersten Bild (sonst ginge die erste Ansage verloren), und ohne
          zwischen Statusregion und erstem Inhalt zu stehen, wo die Kartenregel den oberen Abstand wegnimmt. */}
      <div role="status" aria-live="polite" aria-label="Status: Liste" className={own.nurLeser}>
        {listenAnsage !== undefined ? <span key={listenAnsage.mal}>{listenAnsage.satz}</span> : undefined}
      </div>
    </Card>
  );
}
